import {
  Grid,
  makeStyles,
  Typography,
  Zoom,
  Box,
  Divider,
  Link
} from '@material-ui/core';
import { CheckCircleOutline } from '@material-ui/icons';
import { styled } from '@material-ui/styles';
import moment from 'moment';
import { OnlineOrderDeliverySettings, OnlineOrderingStore } from 'kiolyn-api';
import React, { FC, useEffect, useState } from 'react';
import { useOrdering } from '../../../stores/OrderingStore';
import { isEmpty } from 'lodash';
import {
  displayFullDateMonthFormat,
  displayHourFormat,
  timestampFormat
} from '../../../domain/models/Constants';

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    lineHeight: '26pt'
  },
  icon: {
    margin: theme.spacing(2)
  },
  typo: {
    lineHeight: '45px'
  },
  attention: {
    color: theme.palette.secondary.main
  },
  divider: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4)
  }
}));

const SuccessIcon = styled(CheckCircleOutline)({
  width: 70,
  height: 70
});

interface ThanksProps {
  isSendEmail: boolean;
}

const Thanks: FC<ThanksProps> = ({ isSendEmail }) => {
  const classes = useStyles();
  const orderingStore = useOrdering();
  const { store, order } = orderingStore;
  const [note, setNote] = useState('');
  const onlineStore = orderingStore.store as OnlineOrderingStore;
  const deliverySettings = onlineStore.delivery_settings;
  const deliveryType = deliverySettings?.delivery_type ?? '';
  const defaultDelayTime = 20;

  useEffect(() => {
    if (order.is_asap) {
      if (order.order_type === 'pickup') {
        const duration = store.delay_pickup_time ?? defaultDelayTime;
        setNote(`It's ready for pickup ${duration} minutes from now.`);
      } else if (order.order_type === 'dinein') {
        const duration = store.delay_dinein_time ?? defaultDelayTime;
        setNote(`It's ready for dine-in ${duration} minutes from now.`);
      } else if (order.order_type === 'delivery') {
        if (isEmpty(orderingStore.deliveryDropoffIsoDateTime)) {
          const settings = (orderingStore.store as any)
            .delivery_settings as OnlineOrderDeliverySettings;
          const duration =
            (store.delay_delivery_time ?? defaultDelayTime) +
            (settings.delivery_time ?? 0);
          setNote(
            `It'll be delivered approximately ${duration} minutes from now.`
          );
        } else {
          const dropoff = moment(
            orderingStore.deliveryDropoffIsoDateTime
          ).format('h:mm A');
          setNote(`It'll be delivered approximately at ${dropoff}.`);
        }
      }
    } else {
      let isCatering =
        order.order_type !== 'dinein' &&
        onlineStore.online_order_catering_settings?.enable_online_catering &&
        (((deliveryType === 'postmates' || deliveryType === 'doordash') &&
          order.order_type !== 'delivery') ||
          (deliveryType !== 'postmates' && deliveryType !== 'doordash'));
      let readyDate =
        isCatering &&
        `on ${moment(order.time, timestampFormat).format(
          displayFullDateMonthFormat
        )} `;
      let readyTime = `at ${moment(order.time, timestampFormat).format(
        displayHourFormat
      )}`;
      if (order.order_type === 'pickup') {
        setNote(`It's ready for pickup  ${readyDate}${readyTime}.`);
      } else if (order.order_type === 'dinein') {
        setNote(`It's ready for dine-in ${readyDate}${readyTime}.`);
      } else if (order.order_type === 'delivery') {
        if (!isEmpty(orderingStore.deliveryDropoffIsoDateTime)) {
          readyTime = moment(orderingStore.deliveryDropoffIsoDateTime).format(
            displayHourFormat
          );
        }
        if (isCatering) {
          setNote(`It'll be delivered approximately ${readyDate}${readyTime}.`);
        } else {
          setNote(`It'll be delivered approximately ${readyTime}.`);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNote]);

  const children = (
    <Box>
      <Typography variant="h6" align="center" gutterBottom>
        Thanks for your order!
      </Typography>

      {order.customer_email && isSendEmail && (
        <Typography align="center" gutterBottom>
          We've sent a receipt to{' '}
          <Link href={`mailto: ${order.customer_email}`}>
            {order.customer_email}
          </Link>
          .
          <br />
          Your receipt will come from{' '}
          <span className={classes.attention}>kiolyn.com</span>.
          <br />
          Please make sure it's not in your spam folder.
        </Typography>
      )}
      {!isSendEmail && (
        <Typography align="center" gutterBottom>
          We received your order. However, our email system is currently
          experiencing an issue. Please contact the store if you have questions
          about your order. We apologize for any inconvenience.
        </Typography>
      )}
      <Divider className={classes.divider} />
      <Typography color="secondary" align="center">
        {note}
      </Typography>
    </Box>
  );
  return (
    <Grid
      container
      className={classes.container}
      direction="column"
      alignContent="center"
      alignItems="center"
      justify="center"
    >
      <Zoom in={true} timeout={500}>
        <SuccessIcon color="primary" className={classes.icon} />
      </Zoom>
      {children}
    </Grid>
  );
};

export default Thanks;
